"use client";

import { useCallback, useRef, type RefObject } from "react";
import { adaptInlineFlow, adaptRawFlow } from "./StudioScreenRenderer.flow";
import { isPlainObject } from "./StudioScreenRenderer.utils.data";
import type {
  RunScreenFlow, ScreenExecutionDeps,
} from "./StudioScreenRenderer.execution-types";

const LIFECYCLE_TRIGGERS = new Set(["onMount", "onLoad", "onRouteChange"]);

function stableSerialize(value: unknown): string {
  if (value === null || value === undefined) return "null";
  if (typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(",")}]`;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(record[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(String(value));
}

function getLifecycleContextKey(deps: ScreenExecutionDeps): string {
  return stableSerialize([
    deps.app.slug,
    deps.screen?.manifest.id ?? "",
    deps.route.slug,
    deps.route.params,
    deps.route.query,
  ]);
}

/** Convert a camelCase/PascalCase name to kebab-case ("languageEn" -> "language-en"). */
function toKebabCase(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2")
    .toLowerCase();
}

/** Convert a kebab-case name to camelCase ("language-en" -> "languageEn"). */
function toCamelCase(name: string): string {
  return name
    .toLowerCase()
    .replace(/-+([a-z])/g, (_, character: string) => character.toUpperCase());
}

/**
 * Candidate flow ids for a trigger name.
 *
 * Flow files on disk are commonly authored in kebab-case while atom triggers
 * are authored in camelCase (e.g. trigger "site.languageEn" vs flow id
 * "site.language-en"). The exact name is tried first so existing authoring is
 * unaffected; deterministic case/hyphen variants follow, and a bare lowercase
 * form last so "Site.LanguageEn"-style authoring still resolves.
 */
function getTriggerNameCandidates(name: string): string[] {
  const trimmed = name.trim();
  if (!trimmed) return [];

  const out: string[] = [];
  const push = (candidate: string | undefined): void => {
    if (candidate && candidate.length > 0 && !out.includes(candidate)) {
      out.push(candidate);
    }
  };

  push(trimmed);
  push(toKebabCase(trimmed));
  push(toCamelCase(trimmed));
  push(trimmed.toLowerCase());
  return out;
}

/**
 * Resolve the flow bound to a trigger name.
 *
 * Resolution order (per the DSL contract: triggers "name screen logic or a
 * same-named flow"):
 *  1. The screen's `logic` map: a string value is a flow id resolved through
 *     `app.flows`; any other value is an inline flow/step array.
 *  2. A flow whose id equals the trigger name itself, or a deterministic
 *     case/hyphen variant of it. Widgets composed from mini-app flow files
 *     (e.g. the feedback widget's `feedback.selectKind`) are wired purely by
 *     trigger name and never listed in the host screen's `logic` map —
 *     silently returning for those left every widget button inert.
 */
function resolveTriggerRawFlow(
  name: string,
  deps: ScreenExecutionDeps,
): unknown {
  const logicBinding = deps.screen?.logic?.[name];

  if (logicBinding !== undefined) {
    return typeof logicBinding === "string"
      ? deps.app.flows[logicBinding]
      : logicBinding;
  }

  for (const candidate of getTriggerNameCandidates(name)) {
    const raw = deps.app.flows[candidate];
    if (raw !== undefined) return raw;
  }
  return undefined;
}

export function useScreenTriggers(
  executionDeps: RefObject<ScreenExecutionDeps>,
  runFlow: RunScreenFlow,
) {
  const lastLifecycleKeysRef = useRef<Map<string, string>>(new Map());

  return useCallback((name: string, payload: Record<string, unknown> = {}) => {
    const d = executionDeps.current;
    const raw = resolveTriggerRawFlow(name, d);
    if (raw === undefined) {
      d.logger.warn(`Trigger "${name}" has no logic binding or same-named flow`);
      return;
    }

    const flow = (
      isPlainObject(raw) && typeof raw.id === "string"
        ? adaptRawFlow(raw)
        : null
    ) ?? adaptInlineFlow(name, raw);
    if (!flow) {
      d.logger.warn(`Trigger "${name}" has no resolvable flow`);
      return;
    }

    const isLifecycleTrigger = LIFECYCLE_TRIGGERS.has(name);
    const lifecycleKey = isLifecycleTrigger
      ? getLifecycleContextKey(d)
      : undefined;

    if (
      lifecycleKey !== undefined &&
      lastLifecycleKeysRef.current.get(name) === lifecycleKey
    ) {
      return;
    }

    if (lifecycleKey !== undefined) {
      lastLifecycleKeysRef.current.set(name, lifecycleKey);
    }

    void runFlow(flow, payload)
      .then((result) => {
        if (result.ok) return;
        d.logger.error(`Trigger "${name}" flow failed`, result.error);
        if (
          lifecycleKey !== undefined &&
          lastLifecycleKeysRef.current.get(name) === lifecycleKey
        ) {
          lastLifecycleKeysRef.current.delete(name);
        }
      })
      .catch((error: unknown) => {
        d.logger.error(`Trigger "${name}" flow failed`, error);
        if (
          lifecycleKey !== undefined &&
          lastLifecycleKeysRef.current.get(name) === lifecycleKey
        ) {
          lastLifecycleKeysRef.current.delete(name);
        }
      });
  }, [runFlow]);
}